import React, { useEffect, useState, useCallback } from 'react';
import { feedbackAPI } from '../services/api';
import { Table, Badge, Btn, Select, Modal, Card, Pagination, useToast } from '../components/UI';

// ── Type config ───────────────────────────────────────────────────────────────
const TYPE_META = {
  feedback:   { icon: '💬', label: 'Feedback',   color: 'var(--blue)',  bg: 'var(--blue-bg)' },
  bug_report: { icon: '🐛', label: 'Bug report',  color: 'var(--red)',   bg: 'var(--red-bg)' },
  suggestion: { icon: '💡', label: 'Suggestion',  color: 'var(--gold)',  bg: 'var(--gold-bg)' },
};

const STATUS_META = {
  open:      { label: 'Open',      color: 'var(--blue)',   bg: 'var(--blue-bg)' },
  in_review: { label: 'In review', color: 'var(--gold)',   bg: 'var(--gold-bg)' },
  resolved:  { label: 'Resolved',  color: 'var(--accent)', bg: 'var(--accent-light)' },
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Feedback() {
  const { show, ToastEl } = useToast();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [meta, setMeta]         = useState({});
  const [statusF, setStatusF]   = useState('');
  const [typeF, setTypeF]       = useState('');
  const [viewItem, setViewItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await feedbackAPI.list({ page, limit: 15, status: statusF, type: typeF });
      setItems(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load feedback', 'error'); }
    finally { setLoading(false); }
  }, [page, statusF, typeF]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id, status) {
    try {
      await feedbackAPI.updateStatus(id, status);
      show('Status updated');
      load();
      // Keep view modal in sync
      if (viewItem?.id === id) setViewItem(v => v ? { ...v, status } : v);
    } catch { show('Failed to update', 'error'); }
  }

  const columns = [
    { key: 'user_name', label: 'Submitted by', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{row.user_email}</div>
      </div>
    )},
    { key: 'type', label: 'Type', render: v => {
      const m = TYPE_META[v] || TYPE_META.feedback;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, padding: '2px 9px', borderRadius: 20, background: m.bg, color: m.color }}>
          <span style={{ fontSize: 12 }}>{m.icon}</span> {m.label}
        </span>
      );
    }},
    { key: 'message', label: 'Message', render: v => (
      <span style={{ fontSize: 12, color: 'var(--text2)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 320 }}>
        {v}
      </span>
    )},
    { key: 'status', label: 'Status', render: v => <Badge label={v.replace('_', ' ')} type={v}/> },
    { key: 'created_at', label: 'Date', render: v => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" variant="outline" onClick={e => { e.stopPropagation(); setViewItem(row); }}>View</Btn>
        <Select
          value={row.status}
          onChange={e => updateStatus(row.id, e.target.value)}
          style={{ fontSize: 12, padding: '4px 8px', width: 115 }}
        >
          <option value="open">Open</option>
          <option value="in_review">In review</option>
          <option value="resolved">Resolved</option>
        </Select>
      </div>
    )},
  ];

  // Count by status for summary pills
  const counts = items.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Feedback</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Review and manage feedback submitted by users.</p>
      </div>

      {/* Summary pills */}
      {items.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_META).map(([key, m]) => counts[key] > 0 && (
            <button
              key={key}
              onClick={() => { setStatusF(statusF === key ? '' : key); setPage(1); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                border: `1px solid ${statusF === key ? m.color : 'var(--border)'}`,
                background: statusF === key ? m.bg : 'transparent',
                color: statusF === key ? m.color : 'var(--text2)', cursor: 'pointer',
              }}
            >
              {m.label}
              <span style={{
                background: m.color, color: '#fff',
                borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 600,
              }}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Select value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }} style={{ width: 150 }}>
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_review">In review</option>
          <option value="resolved">Resolved</option>
        </Select>
        <Select value={typeF} onChange={e => { setTypeF(e.target.value); setPage(1); }} style={{ width: 160 }}>
          <option value="">All types</option>
          <option value="feedback">Feedback</option>
          <option value="bug_report">Bug report</option>
          <option value="suggestion">Suggestion</option>
        </Select>
      </div>

      <Card>
        <Table columns={columns} data={items} loading={loading} emptyMsg="No feedback received yet"/>
        <Pagination page={page} pages={meta.pages} total={meta.total} limit={15} onChange={setPage}/>
      </Card>

      {/* View modal */}
      {viewItem && (
        <FeedbackViewModal
          item={viewItem}
          onClose={() => setViewItem(null)}
          onStatusChange={(id, status) => updateStatus(id, status)}
        />
      )}
    </div>
  );
}

// ── Feedback View Modal ───────────────────────────────────────────────────────
function FeedbackViewModal({ item, onClose, onStatusChange }) {
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);
  const typeMeta   = TYPE_META[item.type]   || TYPE_META.feedback;
  const statusMeta = STATUS_META[status]    || STATUS_META.open;

  async function changeStatus(newStatus) {
    setSaving(true);
    try {
      await feedbackAPI.updateStatus(item.id, newStatus);
      setStatus(newStatus);
      onStatusChange(item.id, newStatus);
    } catch {}
    finally { setSaving(false); }
  }

  return (
    <Modal title="" onClose={onClose} width={540}>

      {/* Header */}
      <div style={{
        margin: '-20px -20px 20px', padding: '16px 20px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface2)',
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: typeMeta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, border: `1px solid ${typeMeta.color}33`,
        }}>
          {typeMeta.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: typeMeta.color, marginBottom: 4 }}>
            {typeMeta.label}
          </div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{item.user_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>{item.user_email}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
            background: statusMeta.bg, color: statusMeta.color,
          }}>
            {statusMeta.label}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Message */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 8 }}>
            Message
          </div>
          <div style={{
            background: 'var(--surface2)', borderRadius: 'var(--r)',
            padding: '14px 16px', fontSize: 14, lineHeight: 1.75,
            color: 'var(--text)', whiteSpace: 'pre-wrap',
            borderLeft: `3px solid ${typeMeta.color}`,
            minHeight: 80,
          }}>
            {item.message}
          </div>
        </div>

        {/* Meta */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>Submitted on</div>
            <div style={{ fontSize: 13 }}>
              {new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>Type</div>
            <div style={{ fontSize: 13 }}>{typeMeta.label}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 4 }}>Current status</div>
            <div style={{ fontSize: 13 }}>{statusMeta.label}</div>
          </div>
        </div>

        {/* Status actions */}
        <div style={{ paddingTop: 4, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10 }}>
            Update status
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(STATUS_META).map(([key, m]) => (
              <button
                key={key}
                disabled={saving || status === key}
                onClick={() => changeStatus(key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', borderRadius: 'var(--r)', fontSize: 13, fontWeight: 500,
                  border: `1px solid ${status === key ? m.color : 'var(--border)'}`,
                  background: status === key ? m.bg : 'transparent',
                  color: status === key ? m.color : 'var(--text2)',
                  cursor: saving || status === key ? 'default' : 'pointer',
                  opacity: saving ? .7 : 1, transition: 'all .15s',
                }}
              >
                {status === key && '✓ '}{m.label}
              </button>
            ))}
          </div>
        </div>

      </div>
    </Modal>
  );
}
